import { resolveDirective, openBlock, createElementBlock, normalizeClass, withModifiers, renderSlot, createCommentVNode, createVNode, Transition, withCtx, withDirectives, vShow, normalizeStyle, createElementVNode } from 'vue';
import { t as trapFocus } from './trapFocus-IMs77O3T.js';
import { c as config } from './config-nHWiBviw.js';
import { t as toCssWidth, u as isCustomElement, d as createAbsoluteElement, r as removeElement } from './helpers-O9h6_ICO.js';

const DEFAULT_CLOSE_OPTIONS = ['escape', 'outside'];

const DROPDOWN_INJECTION_KEY = Symbol('bdropdown');

var script$1 = {
    name: 'BDropdown',
    directives: {
        trapFocus
    },
    provide() {
        return {
            [DROPDOWN_INJECTION_KEY]: this
        }
    },
    props: {
        modelValue: {
            type: [String, Number, Boolean, Object, Array, Function],
            default: null
        },
        disabled: Boolean,
        inline: Boolean,
        scrollable: Boolean,
        maxHeight: {
            type: [String, Number],
            default: 200
        },
        position: {
            type: String,
            validator(value) {
                return [
                    'is-top-right',
                    'is-top-left',
                    'is-bottom-left',
                    'is-bottom-right'
                ].indexOf(value) > -1
            }
        },
        triggers: {
            type: Array,
            default: () => ['click']
        },
        mobileModal: {
            type: Boolean,
            default: () => {
                return config.defaultDropdownMobileModal
            }
        },
        ariaRole: {
            type: String,
            validator(value) {
                return [
                    'menu',
                    'list',
                    'dialog'
                ].indexOf(value) > -1
            },
            default: null
        },
        animation: {
            type: String,
            default: 'fade'
        },
        multiple: Boolean,
        trapFocus: {
            type: Boolean,
            default: () => {
                return config.defaultTrapFocus
            }
        },
        closeOnClick: {
            type: Boolean,
            default: true
        },
        canClose: {
            type: [Array, Boolean],
            default: true
        },
        expanded: Boolean,
        appendToBody: Boolean,
        appendToBodyCopyParent: Boolean,
        triggerTabindex: {
            type: Number,
            default: 0
        }
    },
    emits: ['active-change', 'change', 'update:modelValue'],
    data() {
        return {
            selected: this.modelValue,
            style: {},
            isActive: false,
            isHoverable: false,
            maybeTap: false,
            isTouchEnabled: false,
            _bodyEl: undefined, // Used to append to body
            timeOutID: null,
            timeOutID2: null
        }
    },
    computed: {
        rootClasses() {
            return [this.position, {
                'is-disabled': this.disabled,
                'is-hoverable': this.hoverable,
                'is-inline': this.inline,
                'is-active': this.isActive || this.inline,
                'is-mobile-modal': this.isMobileModal,
                'is-expanded': this.expanded,
                'is-touch-enabled': this.isTouchEnabled
            }]
        },
        isMobileModal() {
            return this.mobileModal && !this.inline
        },
        cancelOptions() {
            return typeof this.canClose === 'boolean'
                ? this.canClose
                    ? DEFAULT_CLOSE_OPTIONS
                    : []
                : this.canClose
        },
        contentStyle() {
            return {
                maxHeight: this.scrollable ? toCssWidth(this.maxHeight) : null,
                overflow: this.scrollable ? 'auto' : null
            }
        },
        hoverable() {
            return this.triggers.indexOf('hover') >= 0
        }
    },
    watch: {
        /**
        * When v-model is changed set the new selected item.
        */
        modelValue(value) {
            this.selected = value;
        },

        /**
        * Emit event when isActive value is changed.
        *
        * Also resets `isTouchEnabled` when it turns inactive.
        */
        isActive(value) {
            this.$emit('active-change', value);
            if (!value) {
                // delays to reset the touch enabled flag until the dropdown
                // menu disappears to avoid glitches
                // also takes care of chattering, e.g., repeated quick taps,
                // otherwise the flag may become inconsistent with the actual
                // state of the dropdown menu
                this.timeOutID = setTimeout(() => {
                    if (!this.isActive) {
                        this.isTouchEnabled = false;
                    }
                }, 250);
            }
            this.handleScroll();
            if (this.appendToBody) {
                this.$nextTick(() => {
                    this.updateAppendToBody();
                });
            }
        },

        isHoverable(value) {
            if (this.hoverable) {
                this.$emit('active-change', value);
            }
        }
    },
    methods: {
        handleScroll() {
            if (typeof window === 'undefined') return

            if (this.isMobileModal) {
                if (this.isActive) {
                    document.documentElement.classList.add('is-clipped-touch');
                } else {
                    document.documentElement.classList.remove('is-clipped-touch');
                }
            }
        },

        /**
         * Click listener from DropdownItem.
         *   1. Set new selected item.
         *   2. Emit input event to update the user v-model.
         *   3. Close the dropdown.
         */
        selectItem(value) {
            if (this.multiple) {
                if (this.selected) {
                    if (this.selected.indexOf(value) === -1) {
                        // Add value
                        this.selected = [...this.selected, value];
                    } else {
                        // Remove value
                        this.selected = this.selected.filter((val) => val !== value);
                    }
                } else {
                    this.selected = [value];
                }
                this.$emit('change', this.selected);
            } else {
                if (this.selected !== value) {
                    this.selected = value;
                    this.$emit('change', this.selected);
                }
            }
            this.$emit('update:modelValue', this.selected);
            if (!this.multiple) {
                this.isActive = !this.closeOnClick;
                if (this.hoverable && this.closeOnClick) {
                    this.isHoverable = false;
                }
            }
        },

        /**
        * White-listed items to not close when clicked.
        */
        isInWhiteList(el) {
            if (el === this.$refs.dropdownMenu) return true
            if (el === this.$refs.trigger) return true
            // All chidren from dropdown
            if (this.$refs.dropdownMenu != null) {
                const children = this.$refs.dropdownMenu.querySelectorAll('*');
                for (const child of children) {
                    if (el === child) {
                        return true
                    }
                }
            }
            // All children from trigger
            if (this.$refs.trigger != null) {
                const children = this.$refs.trigger.querySelectorAll('*');
                for (const child of children) {
                    if (el === child) {
                        return true
                    }
                }
            }
            return false
        },

        /**
        * Close dropdown if clicked outside.
        */
        clickedOutside(event) {
            if (this.cancelOptions.indexOf('outside') < 0) return
            if (this.inline) return

            const target = isCustomElement(this) ? event.composedPath()[0] : event.target;
            if (!this.isInWhiteList(target)) this.isActive = false;
        },

        /**
         * Keypress event that is bound to the document
         */
        keyPress({ key }) {
            if (this.isActive && (key === 'Escape' || key === 'Esc')) {
                if (this.cancelOptions.indexOf('escape') < 0) return
                this.isActive = false;
            }
        },

        onClick() {
            // hover precedes
            if (this.triggers.indexOf('hover') !== -1) return
            if (this.triggers.indexOf('click') < 0) return
            this.toggle();
        },
        onContextMenu() {
            if (this.triggers.indexOf('contextmenu') < 0) return
            this.toggle();
        },
        onHover() {
            if (this.triggers.indexOf('hover') < 0) return
            // touch precedes
            if (this.isTouchEnabled) return
            this.isHoverable = true;
        },
        // takes care of touch-enabled devices
        // - does nothing if hover trigger is disabled
        // - suppresses hover trigger by setting isTouchEnabled
        // - handles only a tap; i.e., touchstart on the trigger immediately
        //   folowed by touchend
        onTouchStart() {
            this.maybeTap = true;
        },
        onTouchMove() {
            this.maybeTap = false;
        },
        onTouchEnd(e) {
            if (this.triggers.indexOf('hover') === -1) return
            if (!this.maybeTap) return
            // tap on dropdown contents may happen without preventDefault
            e.preventDefault();
            this.maybeTap = false;
            this.isTouchEnabled = true;
            this.toggle();
        },
        onFocus() {
            if (this.triggers.indexOf('focus') < 0) return
            this.toggle();
        },

        /**
        * Toggle dropdown if it's not disabled.
        */
        toggle() {
            if (this.disabled) return

            if (!this.isActive) {
                // if not active, toggle after clickOutside event
                // this fixes toggling programmatic
                // $nextTick may not wait for other events since Vue 3.
                this.timeOutID2 = setTimeout(() => {
                    const value = !this.isActive;
                    this.isActive = value;
                });
            } else {
                this.isActive = !this.isActive;
            }
        },

        updateAppendToBody() {
            const dropdown = this.$refs.dropdown;
            const dropdownMenu = this.$refs.dropdownMenu;
            const trigger = this.$refs.trigger;
            if (dropdownMenu && trigger) {
                // update wrapper dropdown
                const dropdownWrapper = this.$data._bodyEl.children[0];
                dropdownWrapper.classList.forEach((item) => dropdownWrapper.classList.remove(item));
                dropdownWrapper.classList.add('dropdown');
                dropdownWrapper.classList.add('dropdown-menu-animation');
                // TODO: the following test never becomes true on Vue 3.
                //       I have no idea about the intention of it.
                if (this.$vnode && this.$vnode.data && this.$vnode.data.staticClass) {
                    dropdownWrapper.classList.add(this.$vnode.data.staticClass);
                }
                this.rootClasses.forEach((item) => {
                    // skip position prop
                    if (item && typeof item === 'object') {
                        for (const key in item) {
                            if (item[key]) {
                                dropdownWrapper.classList.add(key);
                            }
                        }
                    }
                });
                if (this.appendToBodyCopyParent) {
                    const parentNode = this.$refs.dropdown.parentNode;
                    const parent = this.$data._bodyEl;
                    parent.classList.forEach((item) => parent.classList.remove(item));
                    parentNode.classList.forEach((item) => {
                        parent.classList.add(item);
                    });
                }
                const rect = trigger.getBoundingClientRect();
                let top = rect.top + window.scrollY;
                let left = rect.left + window.scrollX;
                if (!this.position || this.position.indexOf('bottom') >= 0) {
                    top += trigger.clientHeight;
                } else {
                    top -= dropdownMenu.clientHeight;
                }
                if (this.position && this.position.indexOf('left') >= 0) {
                    left -= (dropdownMenu.clientWidth - trigger.clientWidth);
                }
                this.style = {
                    position: 'absolute',
                    top: `${top}px`,
                    left: `${left}px`,
                    zIndex: '99',
                    width: this.expanded ? `${dropdown.offsetWidth}px` : undefined
                };
            }
        }
    },
    mounted() {
        if (this.appendToBody) {
            this.$data._bodyEl = createAbsoluteElement(this.$refs.dropdownMenu);
            this.updateAppendToBody();
        }
    },
    created() {
        if (typeof window !== 'undefined') {
            document.addEventListener('click', this.clickedOutside);
            document.addEventListener('keyup', this.keyPress);
        }
    },
    beforeUnmount() {
        if (typeof window !== 'undefined') {
            document.removeEventListener('click', this.clickedOutside);
            document.removeEventListener('keyup', this.keyPress);
        }
        if (this.appendToBody) {
            removeElement(this.$data._bodyEl);
        }
        clearTimeout(this.timeOutID);
        clearTimeout(this.timeOutID2);
    }
};

const _hoisted_1$1 = ["tabindex"];
const _hoisted_2$1 = ["aria-hidden"];
const _hoisted_3$1 = ["aria-hidden"];
const _hoisted_4 = ["role", "aria-modal"];

function render$1(_ctx, _cache, $props, $setup, $data, $options) {
  const _directive_trap_focus = resolveDirective("trap-focus");

  return (openBlock(), createElementBlock("div", {
    class: normalizeClass(["dropdown dropdown-menu-animation", $options.rootClasses]),
    ref: "dropdown",
    onMouseleave: _cache[7] || (_cache[7] = $event => ($data.isHoverable = false))
  }, [
    (!$props.inline)
      ? (openBlock(), createElementBlock("div", {
          key: 0,
          tabindex: $props.disabled ? false : $props.triggerTabindex,
          ref: "trigger",
          class: "dropdown-trigger",
          onClick: _cache[0] || (_cache[0] = (...args) => ($options.onClick && $options.onClick(...args))),
          onContextmenu: _cache[1] || (_cache[1] = withModifiers((...args) => ($options.onContextMenu && $options.onContextMenu(...args)), ["prevent"])),
          onMouseenter: _cache[2] || (_cache[2] = (...args) => ($options.onHover && $options.onHover(...args))),
          onFocusCapture: _cache[3] || (_cache[3] = (...args) => ($options.onFocus && $options.onFocus(...args))),
          onTouchstart: _cache[4] || (_cache[4] = (...args) => ($options.onTouchStart && $options.onTouchStart(...args))),
          onTouchmove: _cache[5] || (_cache[5] = (...args) => ($options.onTouchMove && $options.onTouchMove(...args))),
          onTouchend: _cache[6] || (_cache[6] = (...args) => ($options.onTouchEnd && $options.onTouchEnd(...args))),
          "aria-haspopup": "true"
        }, [
          renderSlot(_ctx.$slots, "trigger", { active: $data.isActive })
        ], 40 /* PROPS, HYDRATE_EVENTS */, _hoisted_1$1))
      : createCommentVNode("v-if", true),
    createVNode(Transition, { name: $props.animation }, {
      default: withCtx(() => [
        ($options.isMobileModal)
          ? withDirectives((openBlock(), createElementBlock("div", {
              key: 0,
              class: "background",
              "aria-hidden": !$data.isActive
            }, null, 8 /* PROPS */, _hoisted_2$1)), [
              [vShow, $data.isActive]
            ])
          : createCommentVNode("v-if", true)
      ]),
      _: 1 /* STABLE */
    }, 8 /* PROPS */, ["name"]),
    createVNode(Transition, {
      name: $props.animation,
      persisted: ""
    }, {
      default: withCtx(() => [
        withDirectives((openBlock(), createElementBlock("div", {
          ref: "dropdownMenu",
          class: "dropdown-menu",
          style: normalizeStyle($data.style),
          "aria-hidden": !$data.isActive
        }, [
          createElementVNode("div", {
            class: "dropdown-content",
            role: $props.ariaRole,
            "aria-modal": !$props.inline,
            style: normalizeStyle($options.contentStyle)
          }, [
            renderSlot(_ctx.$slots, "default")
          ], 12 /* STYLE, PROPS */, _hoisted_4)
        ], 12 /* STYLE, PROPS */, _hoisted_3$1)), [
          [vShow, (!$props.disabled && ($data.isActive || $data.isHoverable)) || $props.inline],
          [_directive_trap_focus, $props.trapFocus]
        ])
      ]),
      _: 3 /* FORWARDED */
    }, 8 /* PROPS */, ["name"])
  ], 34 /* CLASS, HYDRATE_EVENTS */))
}

script$1.render = render$1;
script$1.__file = "src/components/dropdown/Dropdown.vue";

var script = {
    name: 'BDropdownItem',
    inject: {
        parent: {
            from: DROPDOWN_INJECTION_KEY,
            default: undefined
        }
    },
    props: {
        value: {
            type: [String, Number, Boolean, Object, Array, Function],
            default: null
        },
        separator: Boolean,
        disabled: Boolean,
        custom: Boolean,
        focusable: {
            type: Boolean,
            default: true
        },
        paddingless: Boolean,
        hasLink: Boolean,
        ariaRole: {
            type: String,
            default: ''
        }
    },
    emits: ['click'],
    computed: {
        anchorClasses() {
            return {
                'is-disabled': this.parent.disabled || this.disabled,
                'is-paddingless': this.paddingless,
                'is-active': this.isActive
            }
        },
        itemClasses() {
            return {
                'dropdown-item': !this.hasLink,
                'is-disabled': this.disabled,
                'is-paddingless': this.paddingless,
                'is-active': this.isActive,
                'has-link': this.hasLink
            }
        },
        ariaRoleItem() {
            return this.ariaRole === 'menuitem' || this.ariaRole === 'listitem' ? this.ariaRole : null
        },
        isClickable() {
            return !this.parent.disabled && !this.separator && !this.disabled && !this.custom
        },
        isActive() {
            if (this.parent.selected === null) return false
            if (this.parent.multiple) return this.parent.selected.indexOf(this.value) >= 0
            return this.value === this.parent.selected
        },
        isFocusable() {
            return this.hasLink ? false : this.focusable
        }
    },
    methods: {
        /**
        * Click listener, select the item.
        */
        selectItem() {
            if (!this.isClickable) return

            this.parent.selectItem(this.value);
            this.$emit('click');
        }
    }
};

const _hoisted_1 = {
  key: 0,
  class: "dropdown-divider"
};
const _hoisted_2 = ["role", "tabindex"];
const _hoisted_3 = ["role", "tabindex"];

function render(_ctx, _cache, $props, $setup, $data, $options) {
  return ($props.separator)
    ? (openBlock(), createElementBlock("hr", _hoisted_1))
    : (!$props.custom && !$props.hasLink)
      ? (openBlock(), createElementBlock("a", {
          key: 1,
          class: normalizeClass(["dropdown-item", $options.anchorClasses]),
          onClick: _cache[0] || (_cache[0] = (...args) => ($options.selectItem && $options.selectItem(...args))),
          role: $options.ariaRoleItem,
          tabindex: $options.isFocusable ? 0 : null
        }, [
          renderSlot(_ctx.$slots, "default")
        ], 10 /* CLASS, PROPS */, _hoisted_2))
      : (openBlock(), createElementBlock("div", {
          key: 2,
          class: normalizeClass($options.itemClasses),
          onClick: _cache[1] || (_cache[1] = (...args) => ($options.selectItem && $options.selectItem(...args))),
          role: $options.ariaRoleItem,
          tabindex: $options.isFocusable ? 0 : null
        }, [
          renderSlot(_ctx.$slots, "default")
        ], 10 /* CLASS, PROPS */, _hoisted_3))
}

script.render = render;
script.__file = "src/components/dropdown/DropdownItem.vue";

export { script as a, script$1 as s };
